// Bestelleingang (docs/bestelleingang/SPEZIFIKATION.md) – Kanäle, Zeiträume und Bezeichnungen für Server UND Oberfläche.
// Der Eingang liest nur, was die Abläufe ohnehin ablegen: die Herkunft am Auftrag (`custom_fields.channel`, SALES_CHANNELS),
// den Bezug zur Veranstaltung (`custom_fields.eventId`) und die offenen Anfragen per E-Mail (sales_mail_request).
import { addDays, type IsoDate } from '@shared/time'
import { SALES_CHANNEL_LABELS, SALES_CHANNELS } from './labels'

/**
 * Kanäle in der Reihenfolge der Oberfläche: die Herkunft eines Auftrags (SALES_CHANNELS) und dazu „Veranstaltung“ – Aufträge
 * ohne Herkunft, die aus der Zusage einer Veranstaltung stammen. Alle übrigen Aufträge ohne Herkunft gelten als „Vertrieb“.
 */
export const INBOX_CHANNELS = ['phone_assistant', 'mail', 'app', 'desk', 'phone', 'event'] as const
export type InboxChannel = (typeof INBOX_CHANNELS)[number]

/** Kurzform an jedem Eintrag – dieselbe wie in Aufträgen, Abholungen und Disposition */
export const INBOX_CHANNEL_LABELS: Record<InboxChannel, string> = { ...SALES_CHANNEL_LABELS, event: 'Veranstaltung' }

/** Beschriftung der Kacheln im Kopf: wer die Bestellung aufgenommen hat */
export const INBOX_CHANNEL_TITLES: Record<InboxChannel, string> = {
  phone_assistant: 'Telefon (KI)',
  mail: 'E-Mail (KI-Agent)',
  app: 'Bestell-App',
  desk: 'Vertrieb (eingetippt)',
  phone: 'Telefon',
  event: 'Veranstaltung',
}

/** Erklärung je Kanal (Tooltip der Kachel, Doku) */
export const INBOX_CHANNEL_DESCRIPTIONS: Record<InboxChannel, string> = {
  phone_assistant: 'Bestellungen, die der Telefonassistent Vocaris angenommen hat – unbestätigt, bis der Vertrieb sie prüft.',
  mail: 'Bestellungen aus E-Mails: von der Anfragen-Zentrale erkannt – als Auftrag übernommen oder noch offen.',
  app: 'Bestellungen aus der Bestell-App (Shop, Geschäftskunden-Portal, Abo).',
  desk: 'Im Vertrieb eingetippte Aufträge – auch alle Aufträge ohne angegebene Herkunft.',
  phone: 'Vom Personal am Telefon erfasste Bestellungen.',
  event: 'Aufträge aus der Zusage einer Veranstaltung.',
}

/** Gespeicherte Herkunft, die der Eingang als Kanal übernimmt (alles andere: Veranstaltung bzw. Vertrieb) */
export const STORED_CHANNELS: readonly string[] = SALES_CHANNELS

export const INBOX_PERIODS = ['today', '7d', '30d'] as const
export type InboxPeriod = (typeof INBOX_PERIODS)[number]
export const INBOX_PERIOD_LABELS: Record<InboxPeriod, string> = { today: 'Heute', '7d': '7 Tage', '30d': '30 Tage' }
/** Vorgabe der Seite und Zeitraum der Plakette in der Navigation */
export const DEFAULT_INBOX_PERIOD: InboxPeriod = '7d'
const PERIOD_DAYS: Record<InboxPeriod, number> = { today: 1, '7d': 7, '30d': 30 }

/** Erster Geschäftstag des Zeitraums – heute zählt mit („7 Tage“ = heute und die sechs Tage davor). */
export const periodStartDay = (today: IsoDate, period: InboxPeriod): IsoDate => addDays(today, 1 - PERIOD_DAYS[period])

/** „Zu prüfen“ = unbestätigte Aufträge und offene Bestell-E-Mails · „Alle“ = alles im Zeitraum */
export const INBOX_VIEWS = ['review', 'all'] as const
export type InboxView = (typeof INBOX_VIEWS)[number]

/** Zustand einer Bestell-E-Mail, die noch nicht als Auftrag übernommen ist (eigene Zeilenart „E-Mail – noch nicht übernommen“) */
export const MAIL_PENDING_STATUS = 'mail_pending'
export const MAIL_PENDING_LABEL = 'Noch nicht übernommen'

/** Höchstzahl der Einträge je Abruf (Zähler zählen immer alles im Zeitraum) */
export const INBOX_ROW_LIMIT = 300

export const INBOX_HINT_TONES = ['info', 'warning', 'danger'] as const
export type InboxHintTone = (typeof INBOX_HINT_TONES)[number]

export interface InboxHint {
  /** phone_<Hinweis>, checkout_<Hinweis>, price, rentals, commission, mail_customer, mail_lines, mail_sender, mail_flagged */
  code: string
  label: string
  tone: InboxHintTone
}

/** Satz der Suche: alle Wörter müssen vorkommen (höchstens fünf), Platzhalter des Musters entschärft. */
export function searchPatterns(q: string | null | undefined): string[] {
  return (q ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 5)
    .map((word) => `%${word.replace(/[\\%_]/g, (ch) => `\\${ch}`)}%`)
}
